import type { State } from "./types";

export type ChartThemeName = "light" | "dark" | "moon" | "summer" | "matrix" | "lovely";

export type ChartTheme = {
  background: string;
  panelBackground: string;
  panelBorder: string;
  grid: string;
  text: string;
  mutedText: string;
  crosshair: string;
  crosshairLabelBg: string;
  crosshairLabelText: string;
  up: string;
  down: string;
  wick: string;
  axisBand: string;
  tagText: string;
};

export type ChartThemeOverrides = Partial<ChartTheme>;
export type ChartThemeChangeDetail = {
  themeName: ChartThemeName;
};

export const CHART_THEME_CHANGE_EVENT = "chart-learning:theme-change";

export const THEMES: Record<ChartThemeName, ChartTheme> = {
  light: {
    background: "#ffffff",
    panelBackground: "#ffffff",
    panelBorder: "#e5e7eb",
    grid: "#d9d9d9",
    text: "#111827",
    mutedText: "#64748b",
    crosshair: "#64748b",
    crosshairLabelBg: "#111827",
    crosshairLabelText: "#ffffff",
    up: "#199d69",
    down: "#ef4444",
    wick: "#666666",
    axisBand: "#ffffff",
    tagText: "#ffffff",
  },
  dark: {
    background: "#0f111a",
    panelBackground: "#161922",
    panelBorder: "#262b36",
    grid: "#262b36",
    text: "#ffffff",
    mutedText: "#a8b0be",
    crosshair: "#a8b0be",
    crosshairLabelBg: "#ffffff",
    crosshairLabelText: "#0f111a",
    up: "#199d69",
    down: "#f87171",
    wick: "#a8b0be",
    axisBand: "#161922",
    tagText: "#ffffff",
  },
  moon: {
    background: "#0b1022",
    panelBackground: "#111833",
    panelBorder: "#1e2a4a",
    grid: "#243458",
    text: "#d7def2",
    mutedText: "#8ea0cf",
    crosshair: "#6fa3ff",
    crosshairLabelBg: "#d7def2",
    crosshairLabelText: "#0b1022",
    up: "#35d6b8",
    down: "#ff7fa8",
    wick: "#9eb0dd",
    axisBand: "#111833",
    tagText: "#ffffff",
  },
  summer: {
    background: "#fffdf5",
    panelBackground: "#fff7e6",
    panelBorder: "#f0e1b8",
    grid: "#f0e6cc",
    text: "#3b2f1d",
    mutedText: "#7c6a4e",
    crosshair: "#c0841f",
    crosshairLabelBg: "#3b2f1d",
    crosshairLabelText: "#fff7e6",
    up: "#0f9d58",
    down: "#d93025",
    wick: "#8c7756",
    axisBand: "#fff7e6",
    tagText: "#ffffff",
  },
  matrix: {
    background: "#0a0f0d",
    panelBackground: "#111814",
    panelBorder: "#1f2a22",
    grid: "#1d2a21",
    text: "#8fffaa",
    mutedText: "#4dbb68",
    crosshair: "#35ff72",
    crosshairLabelBg: "#8fffaa",
    crosshairLabelText: "#0a0f0d",
    up: "#35ff72",
    down: "#ff466d",
    wick: "#77dd93",
    axisBand: "#111814",
    tagText: "#0a0f0d",
  },
  lovely: {
    background: "#fff5fb",
    panelBackground: "#ffeef8",
    panelBorder: "#f8d5ea",
    grid: "#f4dbe8",
    text: "#4a1f36",
    mutedText: "#8f5574",
    crosshair: "#d9468f",
    crosshairLabelBg: "#4a1f36",
    crosshairLabelText: "#fff5fb",
    up: "#10b981",
    down: "#f43f5e",
    wick: "#b87b9b",
    axisBand: "#ffeef8",
    tagText: "#ffffff",
  },
};

const THEME_OVERRIDES: Partial<Record<ChartThemeName, ChartThemeOverrides>> = {};

function mergeTheme(themeName: ChartThemeName): ChartTheme {
  return {
    ...THEMES[themeName],
    ...(THEME_OVERRIDES[themeName] ?? {}),
  };
}

export function getTheme(state: State): ChartTheme {
  return mergeTheme(state.themeName);
}

export function getThemeByName(themeName: ChartThemeName): ChartTheme {
  return mergeTheme(themeName);
}

export function setThemeOverrides(themeName: ChartThemeName, overrides: ChartThemeOverrides): void {
  THEME_OVERRIDES[themeName] = {
    ...(THEME_OVERRIDES[themeName] ?? {}),
    ...overrides,
  };
}

export function clearThemeOverrides(themeName: ChartThemeName): void {
  delete THEME_OVERRIDES[themeName];
}

export function getThemeOverrides(themeName: ChartThemeName): ChartThemeOverrides {
  return { ...(THEME_OVERRIDES[themeName] ?? {}) };
}

function hexToRgb(hex: string): string {
  const normalized = hex.replace("#", "");
  const r = Number.parseInt(normalized.slice(0, 2), 16);
  const g = Number.parseInt(normalized.slice(2, 4), 16);
  const b = Number.parseInt(normalized.slice(4, 6), 16);
  return `${r}, ${g}, ${b}`;
}

function mixHex(a: string, b: string, amount: number): string {
  const from = a.replace("#", "");
  const to = b.replace("#", "");
  const out = [0, 2, 4].map((index) => {
    const av = Number.parseInt(from.slice(index, index + 2), 16);
    const bv = Number.parseInt(to.slice(index, index + 2), 16);
    return Math.round(av + (bv - av) * amount)
      .toString(16)
      .padStart(2, "0");
  });
  return `#${out.join("")}`;
}

function isLightTheme(themeName: ChartThemeName): boolean {
  return themeName === "light" || themeName === "summer" || themeName === "lovely";
}

export function applyThemeToInterface(themeName: ChartThemeName): void {
  const t = mergeTheme(themeName);
  const light = isLightTheme(themeName);
  const shadow = light
    ? "0 18px 44px rgba(15, 23, 42, 0.12)"
    : "0 18px 44px rgba(0, 0, 0, 0.32)";
  const cardLightGray = light ? "#ebebeb" : themeName === "dark" ? "#161922" : t.panelBackground;
  const cardLightGrayTwo = light ? "#d9d9d9" : themeName === "dark" ? "#1f232f" : mixHex(t.panelBackground, "#ffffff", 0.1);

  // Update html element for data attribute
  document.documentElement.setAttribute("data-chart-theme", themeName);
  document.body.setAttribute("data-chart-theme", themeName);
  
  // Find all .chart-root elements and apply variables to them
  const chartRoots = document.querySelectorAll('.chart-root');
  chartRoots.forEach((chartRoot) => {
    const root = chartRoot as HTMLElement;
    root.style.setProperty("--chart-bg-main", t.background);
    root.style.setProperty("--chart-bg-card", t.panelBackground);
    root.style.setProperty("--chart-bg-card-light-gray", cardLightGray);
    root.style.setProperty("--chart-bg-card-light-gray-two", cardLightGrayTwo);
    root.style.setProperty("--chart-text-main", t.text);
    root.style.setProperty("--chart-border-main", t.panelBorder);
    root.style.setProperty("--chart-primary", t.up);
    root.style.setProperty("--chart-danger", t.down);
    root.style.setProperty("--chart-success", t.up);
    root.style.setProperty("--chart-bg", t.background);
    root.style.setProperty("--chart-bg-2", t.panelBackground);
    root.style.setProperty("--chart-surface", `${t.panelBackground}f2`);
    root.style.setProperty("--chart-surface-2", light ? cardLightGray : t.panelBackground);
    root.style.setProperty("--chart-surface-3", cardLightGrayTwo);
    root.style.setProperty("--chart-control-bg", light ? "rgba(255, 255, 255, 0.82)" : "rgba(22, 25, 34, 0.86)");
    root.style.setProperty("--chart-control-elevated", light ? "rgba(255, 255, 255, 0.96)" : "rgba(22, 25, 34, 0.96)");
    root.style.setProperty("--chart-control-hover", light ? "rgba(15, 23, 42, 0.055)" : "rgba(255, 255, 255, 0.07)");
    root.style.setProperty("--chart-border", light ? "rgba(15, 23, 42, 0.12)" : t.panelBorder);
    root.style.setProperty("--chart-border-strong", light ? "rgba(15, 23, 42, 0.22)" : mixHex(t.panelBorder, "#ffffff", 0.25));
    root.style.setProperty("--chart-text", t.text);
    root.style.setProperty("--chart-muted", t.mutedText);
    root.style.setProperty("--chart-faint", light ? mixHex(t.mutedText, "#ffffff", 0.15) : mixHex(t.mutedText, "#000000", 0.14));
    root.style.setProperty("--chart-accent", t.down);
    root.style.setProperty("--chart-accent-rgb", hexToRgb(t.down));
    root.style.setProperty("--chart-accent-soft", `rgba(${hexToRgb(t.down)}, ${light ? "0.12" : "0.18"})`);
    root.style.setProperty("--chart-teal", t.up);
    root.style.setProperty("--chart-teal-rgb", hexToRgb(t.up));
    root.style.setProperty("--chart-teal-soft", `rgba(${hexToRgb(t.up)}, ${light ? "0.12" : "0.16"})`);
    root.style.setProperty("--chart-danger", t.down);
    root.style.setProperty("--chart-danger-soft", `rgba(${hexToRgb(t.down)}, 0.12)`);
    root.style.setProperty("--chart-icon-bg", light ? "rgba(15, 23, 42, 0.045)" : "rgba(255, 255, 255, 0.055)");
    root.style.setProperty("--chart-tag-text", t.tagText);
    root.style.setProperty("--chart-chart-bg", t.background);
    root.style.setProperty("--chart-shadow", shadow);
  });

  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent<ChartThemeChangeDetail>(CHART_THEME_CHANGE_EVENT, {
        detail: { themeName },
      }),
    );
  }
}


export function getDrawingDefaultColor(state: State, type: string): string {
  const t = getTheme(state);
  const map: Record<string, string> = {
    vertical: t.crosshair,
    horizontal: t.mutedText,
    ellipse: t.up,
    extendedRectangle: t.up,
    triangle: t.down,
    channel: t.grid,
    fibFan: t.crosshair,
    fibRetrace: t.mutedText,
    fibExtension: t.crosshair,
    polyline: t.down,
    text: t.text,
    priceLabel: t.crosshair,
    trendline: t.crosshair,
    ray: t.crosshair,
  };
  return map[type] ?? t.crosshair;
}

export function getIndicatorFallbackColors(state: State): { primary: string; secondary: string; tertiary: string } {
  const t = getTheme(state);
  return {
    primary: t.crosshair,
    secondary: t.up,
    tertiary: t.down,
  };
}
